import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { AlertTriangle, ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { AnimatedNumber } from '@/components/viz/Kpi'
import { niceScale, prefersReducedMotion, relativeTime, useWidth } from '@/components/viz/utils'
import type { Growth, TrafficByProperty } from '../api/dashboardApi'

/**
 * The dashboard hero: revenue over time above traffic across every property, on one shared time
 * axis and one crosshair. Two panels rather than two y-scales on one plot — revenue and sessions
 * are different units. Toggling the traffic measure or a property morphs the shapes in place;
 * changing the range redraws them. Money is USD; traffic leaves out the excluded countries
 * entirely (the API never sends them).
 */

type Range = '30d' | '90d' | '12m' | 'all'
type Measure = 'sessions' | 'pageviews'
const ranges: { key: Range; label: string }[] = [
  { key: '30d', label: '30D' }, { key: '90d', label: '90D' }, { key: '12m', label: '12M' }, { key: 'all', label: 'All' },
]
const measures: { key: Measure; label: string }[] = [{ key: 'sessions', label: 'Sessions' }, { key: 'pageviews', label: 'Pageviews' }]

// Colour follows the property, in the order lib/analytics-warehouse/properties.js lists them.
const propertyColor: Record<string, string> = {
  arthaus: 'var(--viz-p1)', easel: 'var(--viz-p2)', sites: 'var(--viz-p3)', bio: 'var(--viz-p4)',
  journal: 'var(--viz-p5)', academy: 'var(--viz-p6)', other: 'hsl(var(--muted-foreground))',
}
const colorFor = (key: string) => propertyColor[key] || 'hsl(var(--muted-foreground))'
const REVENUE = 'var(--viz-sessions)'

interface Point {
  key: string; label: string; partial: boolean
  revenue: number; orders: number; storeRevenue: number; storeVisitors: number
  traffic: Record<string, number>; excluded: number; automated: number
}

const utcDay = (day: string) => new Date(`${day}T12:00:00Z`)
const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...opts })
const dayLabel = fmt({ month: 'short', day: 'numeric' })
const monthLabel = fmt({ month: 'short', year: '2-digit' })
const longDay = fmt({ weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
const longMonth = fmt({ month: 'long', year: 'numeric' })

function measureOf(t: TrafficByProperty, key: string, m: Measure) { return t[key]?.[m] ?? 0 }

/** Online-store revenue per store visitor (daily unique visitors, summed). */
const perVisitor = (revenue: number, visitors: number) => visitors ? revenue / visitors : null

/** The buckets a range shows: days, 7-day weeks for 12 months, months for all time. */
function buildPoints(growth: Growth, range: Range, measure: Measure): Point[] {
  const props = growth.properties.map(p => p.key)
  const point = (key: string, label: string, partial = false): Point => ({
    key, label, partial, revenue: 0, orders: 0, storeRevenue: 0, storeVisitors: 0,
    traffic: Object.fromEntries(props.map(p => [p, 0])), excluded: 0, automated: 0,
  })
  const fold = (into: Point, src: { revenue: number; orders: number; storeRevenue: number; traffic: TrafficByProperty; excluded: number; automated: number }) => {
    into.revenue += src.revenue; into.orders += src.orders; into.storeRevenue += src.storeRevenue
    into.storeVisitors += src.traffic.arthaus?.visitors ?? 0
    into.excluded += src.excluded; into.automated += src.automated
    for (const p of props) into.traffic[p] += measureOf(src.traffic, p, measure)
  }
  if (range === 'all') {
    const thisMonth = growth.daily.at(-1)?.day.slice(0, 7)
    return growth.monthly.map(m => {
      const p = point(m.month, monthLabel.format(utcDay(m.month)), m.month.slice(0, 7) === thisMonth)
      fold(p, m)
      return p
    })
  }
  const days = growth.daily.slice(range === '30d' ? -30 : range === '90d' ? -90 : -364)
  if (range !== '12m') {
    return days.map(d => { const p = point(d.day, dayLabel.format(utcDay(d.day))); fold(p, d); return p })
  }
  // 52 whole weeks ending yesterday, so the last bucket is never a part-week dipping to zero.
  const weeks: Point[] = []
  for (let i = 0; i < days.length; i += 7) {
    const p = point(days[i].day, dayLabel.format(utcDay(days[i].day)))
    for (const d of days.slice(i, i + 7)) fold(p, d)
    weeks.push(p)
  }
  return weeks
}

/** Eases an array of numbers from what was shown to the new target; a new shape snaps. */
function useTween(target: number[], shape: string, duration = 750) {
  const [shown, setShown] = useState(target)
  const from = useRef(target)
  const lastShape = useRef(shape)
  useEffect(() => {
    if (lastShape.current !== shape || prefersReducedMotion() || from.current.length !== target.length) {
      lastShape.current = shape; from.current = target; setShown(target); return
    }
    const origin = from.current
    const start = performance.now()
    let frame = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      const e = 1 - Math.pow(1 - t, 3)
      const next = t < 1 ? target.map((v, i) => origin[i] + (v - origin[i]) * e) : target
      from.current = next
      setShown(next)
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target, shape, duration])
  return lastShape.current === shape && shown.length === target.length ? shown : target
}

function smooth(pts: (readonly [number, number])[]) {
  return pts.map(([x, y], i) => {
    if (!i) return `M${x},${y}`
    const [px, py] = pts[i - 1]
    const mid = (x + px) / 2
    return `C${mid},${py} ${mid},${y} ${x},${y}`
  }).join('')
}

function Change({ now, before, label }: { now: number; before: number | null; label: string }) {
  if (before === null) return null
  if (!before) return now ? <span className="text-xs text-muted-foreground">new vs {label}</span> : null
  const change = Math.round(100 * (now - before) / before)
  const Icon = change >= 0 ? ArrowUpRight : ArrowDownRight
  return <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
    <Icon className="h-3.5 w-3.5" aria-hidden /><span className="font-medium text-foreground">{change >= 0 ? '+' : ''}{change}%</span>&nbsp;vs {label}
  </span>
}

function Segmented<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: { key: T; label: string }[]; onChange: (v: T) => void }) {
  return <div className="inline-flex rounded-md border bg-card p-0.5 text-xs" role="group" aria-label={label}>
    {options.map(o => <button key={o.key} type="button" aria-pressed={value === o.key} onClick={() => onChange(o.key)}
      className="rounded px-2.5 py-1 transition-colors aria-[pressed=true]:bg-foreground aria-[pressed=true]:text-background">{o.label}</button>)}
  </div>
}

const countryName = (() => {
  try { const names = new Intl.DisplayNames(['en'], { type: 'region' }); return (c: string) => (c ? names.of(c) ?? c : 'Unknown') } catch { return (c: string) => c || 'Unknown' }
})()

export function GrowthHero({ growth }: { growth: Growth }) {
  const [range, setRange] = useState<Range>('30d')
  const [measure, setMeasure] = useState<Measure>('sessions')
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [hover, setHover] = useState<number | null>(null)
  const [ref, width] = useWidth<HTMLDivElement>()

  const currency = growth.currency || 'USD'
  const money = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }), [currency])
  const cents = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }), [currency])
  const symbol = money.formatToParts(0).find(p => p.type === 'currency')?.value ?? ''
  const moneyAxis = (n: number) => n >= 1000 ? `${symbol}${(n / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k` : `${symbol}${Math.round(n)}`
  const countAxis = (n: number) => n >= 1000 ? `${(n / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k` : String(Math.round(n))

  const points = useMemo(() => buildPoints(growth, range, measure), [growth, range, measure])
  const visible = growth.properties.filter(p => !hidden.has(p.key))

  // Totals for the headline and the comparison with the window before (days only).
  const totals = points.reduce((t, p) => ({
    revenue: t.revenue + p.revenue, orders: t.orders + p.orders,
    storeRevenue: t.storeRevenue + p.storeRevenue, storeVisitors: t.storeVisitors + p.storeVisitors,
    traffic: t.traffic + visible.reduce((n, v) => n + p.traffic[v.key], 0),
    excluded: t.excluded + p.excluded, automated: t.automated + p.automated,
  }), { revenue: 0, orders: 0, storeRevenue: 0, storeVisitors: 0, traffic: 0, excluded: 0, automated: 0 })
  const span = range === '30d' ? 30 : range === '90d' ? 90 : 0
  const prior = span && growth.daily.length >= 2 * span ? growth.daily.slice(-2 * span, -span) : null
  const priorRevenue = prior ? prior.reduce((n, d) => n + d.revenue, 0) : null
  const priorOrders = prior ? prior.reduce((n, d) => n + d.orders, 0) : null
  const priorTraffic = prior ? prior.reduce((n, d) => n + visible.reduce((m, v) => m + measureOf(d.traffic, v.key, measure), 0), 0) : null
  const aov = totals.orders ? totals.revenue / totals.orders : 0
  const rpv = perVisitor(totals.storeRevenue, totals.storeVisitors)
  const priorRpv = prior ? perVisitor(prior.reduce((n, d) => n + d.storeRevenue, 0), prior.reduce((n, d) => n + (d.traffic.arthaus?.visitors ?? 0), 0)) : null

  // Geometry: one SVG, revenue panel over traffic panel, a shared x.
  const pad = { left: 52, right: 14 }
  const revTop = 22
  const revH = 190
  const trafTop = revTop + revH + 46
  const trafH = 150
  const height = trafTop + trafH + 28
  const plotW = Math.max(0, width - pad.left - pad.right)
  const n = points.length
  const x = (i: number) => pad.left + (n > 1 ? (i / (n - 1)) * plotW : plotW / 2)

  const revScale = niceScale(Math.max(0, ...points.map(p => p.revenue)))
  const stackTops = points.map(p => visible.reduce((s, v) => s + p.traffic[v.key], 0))
  const trafScale = niceScale(Math.max(0, ...stackTops))
  // Everything that moves is tweened together: values plus both scale maxima, so axes glide too.
  const target = useMemo(() => [
    ...points.map(p => p.revenue), revScale.max,
    ...visible.flatMap(v => points.map(p => p.traffic[v.key])), trafScale.max,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [points, revScale.max, trafScale.max, visible.map(v => v.key).join()])
  const shape = `${range}|${n}|${visible.map(v => v.key).join()}`
  const tw = useTween(target, shape)
  const revenueNow = tw.slice(0, n)
  const revMax = tw[n] || 1
  const layers = visible.map((_, li) => tw.slice(n + 1 + li * n, n + 1 + (li + 1) * n))
  const trafMax = tw[n + 1 + visible.length * n] || 1
  const yRev = (v: number) => revTop + revH - (v / revMax) * revH
  const yTraf = (v: number) => trafTop + trafH - (v / trafMax) * trafH

  const revPts = revenueNow.map((v, i) => [x(i), yRev(v)] as const)
  const revLine = smooth(revPts)
  const revArea = n ? `${revLine}L${x(n - 1)},${revTop + revH}L${x(0)},${revTop + revH}Z` : ''
  const cumulative = layers.reduce<number[][]>((acc, layer) => {
    const below = acc.at(-1) ?? points.map(() => 0)
    acc.push(layer.map((v, i) => below[i] + v))
    return acc
  }, [])
  const bands = visible.map((v, li) => {
    const top = cumulative[li].map((c, i) => [x(i), yTraf(c)] as const)
    const bottom = (li ? cumulative[li - 1] : points.map(() => 0)).map((c, i) => [x(i), yTraf(c)] as const).reverse()
    const upper = smooth(top)
    const lower = smooth(bottom).replace(/^M/, 'L')
    return { key: v.key, upper, area: n ? `${upper}${lower}Z` : '' }
  })

  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(plotW / 76))))
  const pick = (e: PointerEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setHover(Math.max(0, Math.min(n - 1, Math.round(((e.clientX - rect.left) / rect.width) * (n - 1)))))
  }
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === 'ArrowRight') setHover(h => Math.min(n - 1, (h ?? -1) + 1))
    else if (e.key === 'ArrowLeft') setHover(h => Math.max(0, (h ?? n) - 1))
    else if (e.key === 'Escape') setHover(null)
    else return
    e.preventDefault()
  }
  const hp = hover === null ? null : points[hover]
  const toggle = (key: string) => setHidden(h => {
    const next = new Set(h)
    if (next.has(key)) next.delete(key)
    else if (visible.length > 1) next.add(key)
    return next
  })

  const rangeLabel = range === '30d' ? 'last 30 days' : range === '90d' ? 'last 90 days' : range === '12m' ? 'last 12 months' : 'all time'
  const priorLabel = range === '30d' ? 'prior 30 days' : 'prior 90 days'
  const neverRan = !growth.sources.shopify.lastSuccess && !growth.sources.posthog.lastSuccess
  const problems = (['shopify', 'posthog'] as const).filter(s => growth.sources[s].lastError)
  const measureLabel = measure === 'sessions' ? 'Sessions' : 'Pageviews'
  const titleFor = (p: Point) => range === 'all' ? `${longMonth.format(utcDay(p.key))}${p.partial ? ' (to date)' : ''}`
    : range === '12m' ? `Week from ${longDay.format(utcDay(p.key))}` : longDay.format(utcDay(p.key))

  return <Card className="viz-rise relative overflow-hidden">
    <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(circle at 0% 0%, color-mix(in oklab, ${REVENUE} 12%, transparent), transparent 45%), radial-gradient(circle at 100% 100%, color-mix(in oklab, var(--viz-p1) 8%, transparent), transparent 40%)` }} />
    <CardContent className="relative space-y-6 p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="text-sm uppercase tracking-wider text-muted-foreground">Revenue · {rangeLabel}</p>
          <p className="mt-2 font-serif text-5xl font-semibold leading-none"><AnimatedNumber value={totals.revenue} format={v => money.format(v)} /></p>
          <div className="mt-2 min-h-5"><Change now={totals.revenue} before={priorRevenue} label={priorLabel} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented label="Window" value={range} options={ranges} onChange={r => { setRange(r); setHover(null) }} />
          <Segmented label="Traffic measure" value={measure} options={measures} onChange={setMeasure} />
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ['Orders', <AnimatedNumber key="o" value={totals.orders} />, <Change key="oc" now={totals.orders} before={priorOrders} label={priorLabel} />],
          ['Average order', <AnimatedNumber key="a" value={aov} format={v => money.format(v)} />, <span key="ac" className="text-xs text-muted-foreground">subtotal, before shipping & tax</span>],
          [`${measureLabel}, all properties`, <AnimatedNumber key="t" value={totals.traffic} />, <Change key="tc" now={totals.traffic} before={priorTraffic} label={priorLabel} />],
          ['Revenue per visitor', rpv === null ? <span key="r">—</span> : <AnimatedNumber key="r" value={Math.round(rpv * 100) / 100} format={v => cents.format(v)} />,
            prior ? <Change key="rc" now={rpv ?? 0} before={priorRpv} label={priorLabel} /> : <span key="rc" className="text-xs text-muted-foreground">online-store revenue ÷ store visitors</span>],
        ].map(([label, value, note]) => <div key={label as string} className="rounded-lg border bg-background/60 p-3 backdrop-blur-sm">
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd className="mt-0.5 font-serif text-2xl font-semibold tabular-nums">{value}</dd>
          <dd className="min-h-4">{note}</dd>
        </div>)}
      </dl>

      <div>
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs" role="group" aria-label="Properties">
          <span className="flex items-center gap-2 text-muted-foreground"><span className="h-0.5 w-4 rounded-full" style={{ background: REVENUE }} />Revenue</span>
          <span aria-hidden className="h-3 w-px bg-border" />
          {growth.properties.map(p => <button key={p.key} type="button" aria-pressed={!hidden.has(p.key)} onClick={() => toggle(p.key)}
            className="flex items-center gap-1.5 rounded px-1 py-0.5 text-muted-foreground transition-opacity hover:text-foreground aria-[pressed=false]:opacity-40">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colorFor(p.key) }} />{p.label}
          </button>)}
        </div>

        <div ref={ref} className="relative">
          {width > 0 && <svg width={width} height={height} tabIndex={0} onKeyDown={onKey} onBlur={() => setHover(null)} role="img"
            aria-label={`Revenue and ${measureLabel.toLowerCase()} by property, ${rangeLabel}. Use left and right arrow keys to read each point.`}
            className="overflow-visible rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <defs>
              <linearGradient id="growth-revenue" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={REVENUE} stopOpacity={0.32} /><stop offset="100%" stopColor={REVENUE} stopOpacity={0} />
              </linearGradient>
            </defs>
            <text x={pad.left} y={revTop - 8} className="fill-muted-foreground text-[11px] font-medium uppercase tracking-wider">Revenue</text>
            <text x={pad.left} y={trafTop - 8} className="fill-muted-foreground text-[11px] font-medium uppercase tracking-wider">{measureLabel} by property</text>
            {revScale.ticks.map(t => <g key={`r${t}`}>
              <line x1={pad.left} x2={pad.left + plotW} y1={yRev(t)} y2={yRev(t)} stroke="hsl(var(--border))" strokeDasharray={t ? '2 4' : undefined} />
              <text x={pad.left - 8} y={yRev(t)} dy="0.32em" textAnchor="end" className="fill-muted-foreground text-[11px] tabular-nums">{moneyAxis(t)}</text>
            </g>)}
            {trafScale.ticks.map(t => <g key={`t${t}`}>
              <line x1={pad.left} x2={pad.left + plotW} y1={yTraf(t)} y2={yTraf(t)} stroke="hsl(var(--border))" strokeDasharray={t ? '2 4' : undefined} />
              <text x={pad.left - 8} y={yTraf(t)} dy="0.32em" textAnchor="end" className="fill-muted-foreground text-[11px] tabular-nums">{countAxis(t)}</text>
            </g>)}

            <g key={`rev-${shape}`}>
              <path d={revArea} fill="url(#growth-revenue)" className="viz-fade" />
              <path d={revLine} fill="none" stroke={REVENUE} strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" pathLength={1} className="viz-draw" />
            </g>
            <g key={`traf-${shape}`} className="viz-fade" style={{ animationDuration: '1.2s' }}>
              {bands.map(b => <path key={b.key} d={b.area} fill={colorFor(b.key)} fillOpacity={0.78} stroke="hsl(var(--card))" strokeWidth={1.5} strokeLinejoin="round" />)}
            </g>

            {points.map((p, i) => (i % every === 0 && x(n - 1) - x(i) >= 60) || i === n - 1
              ? <text key={p.key} x={x(i)} y={height - 6} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} className="fill-muted-foreground text-[11px]">{p.label}</text>
              : null)}

            {hp && hover !== null && <g pointerEvents="none">
              <line x1={x(hover)} x2={x(hover)} y1={revTop} y2={trafTop + trafH} stroke="hsl(var(--foreground))" strokeOpacity={0.22} />
              <circle cx={x(hover)} cy={yRev(revenueNow[hover])} r={5} fill={REVENUE} stroke="hsl(var(--card))" strokeWidth={2} />
              {visible.map((v, li) => <circle key={v.key} cx={x(hover)} cy={yTraf(cumulative[li][hover])} r={3.5} fill={colorFor(v.key)} stroke="hsl(var(--card))" strokeWidth={1.5} />)}
            </g>}
            <rect x={pad.left} y={revTop} width={plotW} height={trafTop + trafH - revTop} fill="transparent"
              onPointerMove={pick} onPointerDown={pick} onPointerLeave={() => setHover(null)} />
          </svg>}

          {hp && hover !== null && <div role="status" className="pointer-events-none absolute top-0 z-10 min-w-52 rounded-lg border bg-popover/95 px-3 py-2 text-xs shadow-card backdrop-blur"
            style={{ left: Math.min(Math.max(0, x(hover) + (x(hover) > width / 2 ? -228 : 16)), Math.max(0, width - 212)) }}>
            <p className="mb-1.5 font-medium">{titleFor(hp)}</p>
            <p className="flex items-center justify-between gap-4"><span className="flex items-center gap-2 text-muted-foreground"><span className="h-2 w-2 rounded-full" style={{ background: REVENUE }} />Revenue</span><span className="font-medium tabular-nums">{money.format(hp.revenue)}</span></p>
            <p className="flex justify-between gap-4 text-muted-foreground"><span className="pl-4">Orders</span><span className="tabular-nums">{hp.orders}</span></p>
            <p className="flex justify-between gap-4 text-muted-foreground"><span className="pl-4">Per store visitor</span><span className="tabular-nums">{hp.storeVisitors ? cents.format(hp.storeRevenue / hp.storeVisitors) : '—'}</span></p>
            <div className="my-1.5 h-px bg-border" />
            {[...visible].reverse().map(v => <p key={v.key} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-2 text-muted-foreground"><span className="h-2 w-2 rounded-sm" style={{ background: colorFor(v.key) }} />{v.label}</span>
              <span className="font-medium tabular-nums">{hp.traffic[v.key].toLocaleString()}</span>
            </p>)}
          </div>}
        </div>
      </div>

      <div className="grid gap-4 border-t pt-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <div className="space-y-1.5 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">What the traffic counts</p>
          <p>Real visits only. Traffic from {growth.excludedLabel} is left out of every figure, along with bots, crawlers and headless browsers
            — <span className="tabular-nums">{totals.excluded.toLocaleString()}</span> sessions and <span className="tabular-nums">{totals.automated.toLocaleString()}</span> automated pageviews, {rangeLabel}.</p>
          <p>Revenue per visitor is online-store revenue ÷ Arthaus store visitors (daily unique visitors, summed).</p>
        </div>
        {growth.countries.length > 0 && <div>
          <p className="mb-2 text-xs font-medium">Top countries by sessions, last 30 days</p>
          <ul className="space-y-1.5">
            {growth.countries.map((c, i) => {
              const max = Math.max(1, ...growth.countries.map(x => x.sessions))
              return <li key={c.country || 'unknown'} className="grid grid-cols-[8rem_1fr_auto] items-center gap-3 text-xs">
                <span className="truncate">{countryName(c.country)}</span>
                <div className="h-2 overflow-hidden rounded-full" style={{ background: 'var(--viz-empty)' }}>
                  <div className="viz-grow-x h-full rounded-full" style={{ width: `${100 * c.sessions / max}%`, background: 'var(--viz-p1)', animationDelay: `${50 * i}ms` }} />
                </div>
                <span className="w-12 text-right font-medium tabular-nums">{c.sessions.toLocaleString()}</span>
              </li>
            })}
          </ul>
        </div>}
      </div>

      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View as table</summary>
        <div className="mt-3 max-h-72 overflow-auto rounded-md border">
          <table className="w-full text-left tabular-nums">
            <thead className="sticky top-0 bg-card"><tr className="border-b">
              <th className="px-2 py-1.5 font-medium">Period</th><th className="px-2 py-1.5 text-right font-medium">Revenue</th><th className="px-2 py-1.5 text-right font-medium">Orders</th>
              {visible.map(v => <th key={v.key} className="px-2 py-1.5 text-right font-medium">{v.label}</th>)}
            </tr></thead>
            <tbody>{[...points].reverse().map(p => <tr key={p.key} className="border-b last:border-0">
              <td className="px-2 py-1">{titleFor(p)}</td><td className="px-2 py-1 text-right">{money.format(p.revenue)}</td><td className="px-2 py-1 text-right">{p.orders}</td>
              {visible.map(v => <td key={v.key} className="px-2 py-1 text-right">{p.traffic[v.key].toLocaleString()}</td>)}
            </tr>)}</tbody>
          </table>
        </div>
      </details>

      {(neverRan || problems.length > 0) && <div role="alert" className="flex items-start gap-2 rounded-lg border p-3 text-xs" style={{ borderColor: 'color-mix(in oklab, var(--viz-warning) 50%, transparent)' }}>
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: 'var(--viz-warning)' }} aria-hidden />
        <div>
          {neverRan && <p><span className="font-medium">The analytics batch has not run yet.</span> It runs nightly; to fill history now, run <code>node scripts/analytics-warehouse.js --from 2021-01-01</code>.</p>}
          {problems.map(s => <p key={s}><span className="font-medium">{s === 'shopify' ? 'Shopify revenue' : 'PostHog traffic'} failed on its last run:</span> {growth.sources[s].lastError}</p>)}
        </div>
      </div>}
    </CardContent>
    <p className="relative px-6 pb-4 text-xs text-muted-foreground md:px-8">
      Every Shopify order placed, by the day it was placed (Central time), test and cancelled orders left out; revenue is the order subtotal after discounts and refunds, before shipping and tax,
      in US dollars — what the customer paid for orders paid in dollars, the day&apos;s exchange rate for the rest.
      Traffic is PostHog pageviews across every Arthaus property. Updated nightly through yesterday
      {growth.sources.shopify.lastSuccess && ` · Shopify ${relativeTime(growth.sources.shopify.lastSuccess)}`}
      {growth.sources.posthog.lastSuccess && ` · PostHog ${relativeTime(growth.sources.posthog.lastSuccess)}`}.
    </p>
  </Card>
}
